import { FormControl, InputLabel, MenuItem, Select, Switch, TextField, Chip, Box } from '@mui/material'
import { useState, useEffect } from 'react'
import { useGetNotificationPrefs, useUpdateNotificationPrefs } from '../api/userSettingsApi'
import { Notifications } from '@mui/icons-material'
import LoadingState from './Common/LoadingState'

const NotificationSettings = () => {
    const { data: prefs, isLoading } = useGetNotificationPrefs();
    const updatePrefs = useUpdateNotificationPrefs();
    const [emailList, setEmailList] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');

    // Update local state when prefs are loaded
    useEffect(() => {
        if (prefs?.additional_emails) {
            if (Array.isArray(prefs.additional_emails)) {
                setEmailList(prefs.additional_emails);
            } else if (typeof prefs.additional_emails === 'string') {
                const emails = prefs.additional_emails.split(',').map((e: string) => e.trim()).filter((e: string) => e);
                setEmailList(emails);
            }
        } else {
            setEmailList([]);
        }
    }, [prefs?.additional_emails]);

    const updateEmails = (emails: string[]) => {
        updatePrefs.mutate({ 
            enabled: prefs?.enabled ?? true,
            frequency: prefs?.frequency || 'hourly',
            additional_emails: emails
        });
    };

    const handleToggle = (enabled: boolean) => {
        updatePrefs.mutate({ 
            enabled, 
            frequency: enabled ? (prefs?.frequency || 'hourly') : 'off',
            additional_emails: emailList
        });
    };

    const handleFrequency = (frequency: 'immediate' | 'hourly' | 'daily' | 'weekly' | 'off') => {
        updatePrefs.mutate({ 
            enabled: frequency !== 'off', 
            frequency,
            additional_emails: emailList
        });
    };

    const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmailsFromInput();
        }
    };

    const addEmailsFromInput = () => {
        if (!emailInput.trim()) return;
        
        const newEmails = emailInput
            .split(',')
            .map(e => e.trim())
            .filter(e => e && !emailList.includes(e)); // Filter out duplicates
        
        if (newEmails.length > 0) {
            const updatedEmails = [...emailList, ...newEmails];
            setEmailList(updatedEmails);
            setEmailInput('');
            updateEmails(updatedEmails);
        }
    };

    const handleDeleteEmail = (emailToDelete: string) => {
        const updatedEmails = emailList.filter(email => email !== emailToDelete);
        setEmailList(updatedEmails);
        updateEmails(updatedEmails);
    };

    return (
        <div className="bg-backgroundGrey w-full h-full flex flex-col overflow-hidden">
            {/* Header Section */}
            <div className="bg-white border-b border-borderGrey px-8 py-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-backgroundBlue rounded-lg">
                        <Notifications className="text-nanonetsBlue" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-greyishBlueMid">Notification Settings</h1>
                        <p className="text-sm text-greyishBlue mt-1">
                            Configure how and when you receive notifications
                        </p>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 overflow-auto px-8 py-6">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <LoadingState />
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-borderGrey p-6 max-w-3xl">
                        {/* Enable Notifications */}
                        <div className="mb-8 pb-6 border-b border-borderGrey">
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <h3 className="text-base font-semibold text-greyishBlueMid mb-1">
                                        Enable Notifications
                                    </h3>
                                    <p className="text-sm text-greyishBlue">
                                        Turn notifications on or off
                                    </p>
                                </div>
                                <Switch 
                                    checked={!!prefs?.enabled} 
                                    onChange={(e) => handleToggle(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: 'nanonetsBlue',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: 'nanonetsBlue',
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        {/* Frequency Selection */}
                        <div className="mb-8 pb-6 border-b border-borderGrey">
                            <h3 className="text-base font-semibold text-greyishBlueMid mb-3">
                                Notification Frequency
                            </h3>
                            <FormControl 
                                sx={{ 
                                    minWidth: 240,
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'nanonetsBlue',
                                        },
                                    },
                                }}
                            >
                                <InputLabel id="notif-frequency-label">Frequency</InputLabel>
                                <Select
                                    labelId="notif-frequency-label"
                                    id="notif-frequency"
                                    value={prefs?.frequency || 'hourly'}
                                    label="Frequency"
                                    onChange={(e) => handleFrequency(e.target.value as any)}
                                    disabled={!prefs?.enabled}
                                >
                                    <MenuItem value={'immediate'}>Immediate</MenuItem>
                                    <MenuItem value={'hourly'}>Hourly</MenuItem>
                                    <MenuItem value={'daily'}>Daily</MenuItem>
                                    <MenuItem value={'weekly'}>Weekly</MenuItem>
                                    <MenuItem value={'off'}>Off</MenuItem>
                                </Select>
                            </FormControl>
                            <p className="text-xs text-greyishBlue mt-2">
                                Choose how often you want to receive notifications
                            </p>
                        </div>

                        {/* Additional Emails */}
                        <div>
                            <h3 className="text-base font-semibold text-greyishBlueMid mb-3">
                                Additional Email Addresses
                            </h3>
                            <p className="text-sm text-greyishBlue mb-4">
                                Add additional email addresses to receive notifications. Press Enter or comma to add them.
                            </p>
                            <TextField
                                id="additional-emails"
                                label="Email Addresses"
                                placeholder="email1@example.com, email2@example.com"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={handleEmailInputKeyDown}
                                onBlur={addEmailsFromInput}
                                variant="outlined"
                                fullWidth
                                disabled={!prefs?.enabled}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'nanonetsBlue',
                                        },
                                    },
                                }}
                            />
                            {emailList.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
                                    {emailList.map((email) => (
                                        <Chip
                                            key={email}
                                            label={email}
                                            onDelete={() => handleDeleteEmail(email)}
                                            className="!bg-backgroundBlueLight !text-nanonetsBlue !border !border-nanonetsBlue/30"
                                            sx={{
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'nanonetsBlue',
                                                    '&:hover': {
                                                        color: 'nanonetsBlue',
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default NotificationSettings
